import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { FileText, Plus, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const Templates = () => {
  const navigate = useNavigate();
  const { API, accessToken } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get(`${API}/templates`, { headers });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      await axios.post(`${API}/templates`, { name: newTemplateName, tags: [] }, { headers });
      toast.success('Template created successfully!');
      setIsDialogOpen(false);
      setNewTemplateName('');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="templates-page">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900">Templates</h1>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Create and manage document templates</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-indigo-600 to-purple-600" data-testid="create-template-btn">
                <Plus size={18} className="mr-2" />
                New Template
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Template</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTemplate} className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Template Name</label>
                  <Input
                    type="text"
                    placeholder="Employment Contract"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    required
                    data-testid="input-template-name"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-template-btn">
                  Create Template
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <Input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-templates"
          />
        </div>

        {/* Templates Grid */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredTemplates.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <Card
                key={template.id}
                className="glass-effect border-none card-shadow hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/templates/${template.id}`)}
                data-testid={`template-${template.id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="text-indigo-600" size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg truncate">{template.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(template.created_at).toLocaleDateString()}
                      </p>
                      <span
                        className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${
                          template.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {template.status}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <FileText className="mx-auto text-gray-400 mb-4" size={64} />
            <p className="text-gray-500 text-lg mb-4">
              {searchQuery ? 'No templates found' : 'No templates yet'}
            </p>
            {!searchQuery && (
              <Button onClick={() => setIsDialogOpen(true)} variant="outline">
                Create Your First Template
              </Button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Templates;